import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Users, Building2, DollarSign, TrendingUp, Mail, Target, CheckSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useI18n } from '../contexts/I18nContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [stats, setStats] = useState({
    contacts_count: 0,
    companies_count: 0,
    deals_count: 0,
    total_deal_value: 0,
    total_revenue: 0,
    conversion_rate: 0,
    campaigns_sent: 0,
    avg_open_rate: 0,
    tasks_pending: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentItems, setRecentItems] = useState({ contacts: [], deals: [], tasks: [] });

  useEffect(() => {
    fetchStats();
    fetchRecentItems();
    
    // Check if we just connected a calendar provider
    const urlParams = new URLSearchParams(window.location.search);
    const calendarConnected = urlParams.get('calendar_connected');
    if (calendarConnected) {
      // Remove the parameter from URL
      window.history.replaceState({}, '', '/dashboard');
      // Redirect to calendar page after a short delay to show connection success
      setTimeout(() => {
        navigate('/calendar?connected=' + calendarConnected);
      }, 500);
    }
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        withCredentials: true,
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentItems = async () => {
    try {
      const [contactsRes, dealsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/contacts`, { withCredentials: true }),
        axios.get(`${API}/deals`, { withCredentials: true }),
        axios.get(`${API}/tasks`, { withCredentials: true }),
      ]);

      setRecentItems({
        contacts: contactsRes.data.slice(0, 5),
        deals: dealsRes.data.slice(0, 5),
        tasks: tasksRes.data.filter(t => t.status !== 'done').slice(0, 5),
      });
    } catch (error) {
      console.error('Error fetching recent items:', error);
    }
  };

  const statCards = [
    {
      title: t('dashboard.totalContacts', 'Total Contacts'),
      value: stats.contacts_count,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: t('dashboard.companies', 'Companies'),
      value: stats.companies_count,
      icon: Building2,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: t('dashboard.activeDeals', 'Active Deals'),
      value: stats.deals_count,
      icon: Target,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
    {
      title: t('dashboard.pendingTasks', 'Pending Tasks'),
      value: stats.tasks_pending,
      icon: CheckSquare,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    {
      title: t('dashboard.totalRevenue', 'Total Revenue'),
      value: `$${stats.total_revenue.toLocaleString()}`,
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: t('dashboard.dealValue', 'Deal Value'),
      value: `$${stats.total_deal_value.toLocaleString()}`,
      icon: TrendingUp,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
    },
    {
      title: t('dashboard.conversionRate', 'Conversion Rate'),
      value: `${stats.conversion_rate}%`,
      icon: TrendingUp,
      color: 'text-pink-600',
      bgColor: 'bg-pink-100',
    },
    {
      title: t('dashboard.campaignsSent', 'Campaigns Sent'),
      value: stats.campaigns_sent,
      icon: Mail,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-100',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0176D3]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('dashboard.title', 'Dashboard')}</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">{t('dashboard.welcome', "Welcome back! Here's what's happening with your business.")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                  {stat.title}
                </CardTitle>
                <div className={`p-1.5 sm:p-2 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 truncate">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.quickActions', 'Quick Actions')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <button 
              onClick={() => navigate('/contacts')}
              className="w-full px-4 py-3 bg-[#0176D3] text-white rounded-lg hover:bg-[#0155A8] transition-colors text-left font-medium"
            >
              + {t('dashboard.addContact', 'Add New Contact')}
            </button>
            <button 
              onClick={() => navigate('/tasks')}
              className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-left font-medium"
            >
              + {t('dashboard.createTask', 'Create Task')}
            </button>
            <button 
              onClick={() => navigate('/deals')}
              className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-left font-medium"
            >
              + {t('dashboard.createDeal', 'Create Deal')}
            </button>
            <button 
              onClick={() => navigate('/marketing')}
              className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-left font-medium"
            >
              + {t('dashboard.newCampaign', 'New Campaign')}
            </button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.recentActivity', 'Recent Activity')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentItems.contacts.length === 0 && recentItems.tasks.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {t('dashboard.noRecent', 'No recent activities')}
                </div>
              ) : (
                <>
                  {recentItems.contacts.slice(0, 3).map(contact => (
                    <div 
                      key={contact.id} 
                      onClick={() => navigate(`/contacts/${contact.id}`)}
                      className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    >
                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600 dark:text-blue-300">
                          {contact.name.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium dark:text-gray-100">{contact.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{contact.company || contact.email}</p>
                      </div>
                      <span className="text-xs text-gray-400">Contact</span>
                    </div>
                  ))}
                  {recentItems.tasks.slice(0, 2).map(task => (
                    <div 
                      key={task.id}
                      onClick={() => navigate('/tasks')}
                      className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    >
                      <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                        <CheckSquare className="w-4 h-4 text-orange-600 dark:text-orange-300" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium dark:text-gray-100">{task.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {task.due_date ? `${t('dashboard.due','Due:')} ${new Date(task.due_date).toLocaleDateString()}` : 'No due date'}
                        </p>
                      </div>
                      <span className="text-xs text-gray-400">Task</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;